import {
  Component,
  computed,
  DestroyRef,
  input,
  OnInit,
  Renderer2,
  Signal,
  signal,
  TemplateRef,
  viewChild,
  WritableSignal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { BehaviorSubject, catchError, combineLatest, EMPTY, finalize, Observable, of, take, tap } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ISidepanelTab } from 'src/app/components/sidepanel-tabs/sidepanel-tabs.component';
import { Label } from 'src/app/labels/englishLabels';
import { IntegrationModel } from 'src/app/models/api';
import {
  AgentStepModel,
  AiService,
  AttachedDocument,
  StepType,
  RagStep,
  CopilotStudioRagStep,
  CopilotStudioTriggerStep,
  CopilotStudioToolInvocationStep,
  ToolInvocationStep,
  ChatGptEnterpriseToolInvocationStep,
  VertexAiRagStep,
  AgentforceRagStep,
  ChatMode,
  ToolInvocationResultStep,
  ToolInvocationRequestStep,
  ChatGptEnterpriseToolInvocationRequestStep,
  ChatGptEnterpriseToolInvocationResultStep,
  ToolRequestStep,
  ToolResponseStep,
  ChatGptEnterpriseToolRequestStep,
  ChatGptEnterpriseToolResponseStep,
  M365CopilotSource,
  AgentHandoffRequestStep,
  AgentHandoffResponseStep,
  BotDetails,
} from 'src/app/models/api/agentStep.model';
import { DetectionRuleSeverity, FindingModel } from 'src/app/models/api/finding.model';
import { JsonHalCollection } from 'src/app/models/api/jsonhal.model';
import { AiAgentStepActivityTableRowViewModel } from 'src/app/models/viewModels/aiAgentStepActivityTableRowViewModel';
import { DisplayCamelCaseStringPipe } from 'src/app/pipes/display-camel-case-string/display-camel-case-string.pipe';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import {
  ActionGetAgentStepSensitiveDataResponseModel,
  AiAgentStepsHttpService,
  SensitiveDataRequestActions,
} from 'src/app/services/httpServices/detection/aiAgentStepsHttpService/ai-agent-steps-http.service';
import { AiFindingsHttpService } from 'src/app/services/httpServices/detection/aiFindingsHttpService/ai-findings-http.service';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AgentStepsActionsService } from 'src/app/services/actions/agent-steps/agent-steps-actions.service';
import { AgentStepsSupportedActions } from 'src/app/services/actions/agent-steps/agent-steps-supported-actions';
import { MenuAction } from 'src/app/models/menuAction.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NotificationsService } from '../../../services/infra/notifications/notifications.service';
import { ICardField } from 'src/app/models/viewModels/cardModel';
import {
  getAiServiceLogoPath,
  getAiServiceDisplayName,
  getClientApplicationIconPath,
  isM365CustomAgent,
} from 'src/app/common/aiServiceUtils';
import { CitedSource } from './ai-agent-citation.component';
import { resolveCitationResourceType } from './citation-utils';
import { SearchActivityHttpService } from 'src/app/services/httpServices/detection/searchActivityService/search-activity-http.service';
import {
  InLineActivityStepTableRowViewModel,
  transformInLineActivityStepToTableRowViewModel,
} from 'src/app/models/viewModels/InLineActivityStepTableRowView.model';
import { InLineActivityStepModel } from 'src/app/models/api/inLineActivityStep.model';
import { map } from 'rxjs/operators';
import { SidePanelService } from 'src/app/features/side-panel/services/side-panel.service';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { AgentInventoryNavigationService } from 'src/app/services/actions/agent-inventory-navigation.service';

@Component({
  selector: 'app-agent-step',
  templateUrl: './enterprise-ai-agent-step.component.html',
  styleUrls: ['./enterprise-ai-agent-step.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentStepComponent implements OnInit {
  integration = input.required<string>();
  agentStep = input.required<string>();
  stepThreadId = input.required<string>();
  viewModel = input<AiAgentStepActivityTableRowViewModel | InLineActivityStepTableRowViewModel | null>(null);
  inline = input<string | null>(null);

  public errorMessage = '';
  public isLoading$ = new BehaviorSubject<boolean>(true);
  public isThreadIdsLoading$ = new BehaviorSubject<boolean>(true);
  public step: AgentStepModel;
  public integrationName: string;
  public showNoData = false;
  public tabs: ISidepanelTab[];
  public activeTabIndex: WritableSignal<number> = signal(0);

  public hasRelatedFindings = computed(() => {
    if (!this.isInlineView()) return false;

    const vm = this.viewModel();
    if (vm) {
      if ('relatedFindings' in vm && vm.relatedFindings?.length > 0) {
        return true;
      }
      if ('blockedStepIcon' in vm && vm.blockedStepIcon) {
        return true;
      }
    }

    const inlineData = this._inlineStepData();
    return inlineData != null && (inlineData.relatedFindings?.length ?? 0) > 0;
  });

  public attachments: WritableSignal<AttachedDocument[]> = signal([]);
  public attachmentTemplate = viewChild('attachmentTemplate', { read: TemplateRef });
  public attachmentActions: MenuAction[];
  public attachmentCardField: Signal<ICardField>;
  public customAgentTemplate = viewChild('customAgentTemplate', { read: TemplateRef });
  public findingsTemplate = viewChild('findingsTemplate', { read: TemplateRef });

  public stepDetailsCardFields: ICardField[];
  public a2aCommunicationCardFields: ICardField[] = [];
  public threadCardFields: ICardField[] = [];
  public actorCardFieldList: ICardField[][];
  public clientCardFields: ICardField[];
  public findingsByThreadId: FindingModel[] = [];
  public showThreadSection = false;

  private _inlineStepData: WritableSignal<InLineActivityStepTableRowViewModel | null> = signal(null);

  public menuActions$ = new BehaviorSubject<MenuAction[]>([]);

  public isActionRunningSpinner = false;

  protected citedSources: WritableSignal<CitedSource[]> = signal([]);
  protected sources: WritableSignal<CitedSource[]> = signal([]);
  protected showMoreCitations: WritableSignal<boolean> = signal(false);
  protected showMoreSources: WritableSignal<boolean> = signal(false);

  protected citationAndSourceTabs: Signal<ISidepanelTab[]>;
  protected citationAndSourceActiveTab: WritableSignal<number> = signal(0);

  // Computed signal for findings severities
  public findingsSeverities = computed(() => {
    const severities: Record<DetectionRuleSeverity, number> = {
      [DetectionRuleSeverity.critical]: 0,
      [DetectionRuleSeverity.high]: 0,
      [DetectionRuleSeverity.medium]: 0,
      [DetectionRuleSeverity.low]: 0,
      [DetectionRuleSeverity.informational]: 0,
    };

    if (this.findingsByThreadId && this.findingsByThreadId.length > 0) {
      this.findingsByThreadId.forEach((finding) => {
        const severity = finding.rule.severity;
        if (severity in severities) {
          severities[severity]++;
        }
      });
    }

    return severities;
  });

  private downloadAttachmentAction: MenuAction = {
    name: 'download',
    displayNameLabel: 'commonActions.download.title',
    disabled: false,
  };
  private deleteAttachmentAction: MenuAction = {
    name: 'delete',
    displayNameLabel: 'commonActions.delete',
    disabled: false,
  };

  constructor(
    private _storeService: StoreService,
    private _aiAgentStepsService: AiAgentStepsHttpService,
    private _aiFindingsService: AiFindingsHttpService,
    private _httpErrorHandlerService: HttpErrorHandlerService,
    private _displayCamelCaseStringPipe: DisplayCamelCaseStringPipe,
    private _labelsProviderPipe: LabelsProviderPipe,
    private _router: Router,
    private _route: ActivatedRoute,
    private _agentStepsActionsService: AgentStepsActionsService,
    private _notificationService: NotificationsService,
    private _destroyRef: DestroyRef,
    private _renderer: Renderer2,
    private _searchActivityHttpService: SearchActivityHttpService,
    private _sidePanel: SidePanelService,
    private _featureFlagsService: FeatureFlagsService,
    private _agentInventoryNavigationService: AgentInventoryNavigationService,
    private _httpClient: HttpClient
  ) {
    this.attachmentCardField = computed(() => ({
      key: this._labelsProviderPipe.transform('aiAgentStepPage.attachments'),
      customTemplate: this.attachmentTemplate(),
    }));

    this.citationAndSourceTabs = computed(() => {
      const tabs: ISidepanelTab[] = [];
      const citations = this.citedSources();
      if (citations.length) {
        tabs.push({ labelPath: 'aiAgentStepPage.citedSourcesCardTitle', value: `(${citations.length})` });
      }
      const sources = this.sources();
      if (sources.length) {
        tabs.push({ labelPath: 'aiAgentStepPage.sourcesCardTitle', value: `(${sources.length})` });
      }
      return tabs;
    });
  }

  public ngOnInit() {
    this._init(this.agentStep(), this.stepThreadId(), this.integration(), this.viewModel());
  }

  private _init(
    stepId: string,
    threadId: string,
    integrationId: string,
    stepView: AiAgentStepActivityTableRowViewModel | InLineActivityStepTableRowViewModel | null
  ) {
    let step: AgentStepModel | null = null;
    const isInlineMode = this.isInlineView();

    if (
      stepView &&
      'step' in (stepView as AiAgentStepActivityTableRowViewModel | InLineActivityStepTableRowViewModel)
    ) {
      const maybeStep = (stepView as AiAgentStepActivityTableRowViewModel | InLineActivityStepTableRowViewModel)
        .step as AgentStepModel | undefined;
      if (maybeStep) step = maybeStep;

      if (isInlineMode) {
        if ('relatedFindings' in stepView) {
          this._inlineStepData.set(stepView as InLineActivityStepTableRowViewModel);
        } else if ('params' in stepView || 'result' in stepView || 'transcript' in stepView) {
          this._inlineStepData.set(stepView as unknown as InLineActivityStepTableRowViewModel);
        }
      }
    }

    const needsInlineDataFetch = isInlineMode && !this._inlineStepData();

    const isMockStep = this._isAnyDemoMockStep();

    const step$ =
      step && !needsInlineDataFetch
        ? of(step)
        : isMockStep
          ? step
            ? of(step)
            : this._loadMockStepById(stepId)
          : isInlineMode
            ? this._searchActivityHttpService.findInLineActivityStepByIdViaQuery(stepId).pipe(
                tap((inlineStep: InLineActivityStepModel) => {
                  this._inlineStepData.set(transformInLineActivityStepToTableRowViewModel(inlineStep));
                }),
                map(
                  (inlineStep: InLineActivityStepModel) =>
                    transformInLineActivityStepToTableRowViewModel(inlineStep).step
                )
              )
            : this._aiAgentStepsService.getAiAgentStep(integrationId, stepId);

    combineLatest([step$, this._storeService.integrations$])
      .pipe(
        take(1),
        tap(([stepData, integrations]: [AgentStepModel, IntegrationModel[]]) => {
          this.step = stepData;
          this.integrationName = integrations?.find((i) => i.id === integrationId)?.name || '';
          this._buildStepDetailsCard(stepData);
          this._buildActorCard(stepData);
          this._buildClientCard(stepData);
          this._buildThreadCard(stepData);
          this._setMenuActions(stepData);
        }),
        catchError((err) => {
          this._httpErrorHandlerService.handle(err);
          this.showNoData = true;
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe();

    // Only fetch findings if threadId exists and not null
    if (threadId && threadId !== 'null') {
      if (isMockStep) {
        this._fetchMockFindingsByThreadId(threadId);
      } else {
        this._fetchFindingsByThreadId(integrationId, threadId);
      }
    } else {
      this.isThreadIdsLoading$.next(false);
    }
  }

  public actionSelected(actionString: string) {
    const action = actionString as AgentStepsSupportedActions;
    const response = this._agentStepsActionsService.handleActionClicked(action, this.agentStep(), this.integration());
    response.subscribe({
      next: () => (this.isActionRunningSpinner = false),
    });
    switch (action) {
      case AgentStepsSupportedActions.chatGptEnterpriseDeleteConversationAction:
        this.isActionRunningSpinner = false;
        break;
    }
  }

  public navigateToThreadFindings() {
    if (this.isInlineView()) {
      this._router.navigate(['app/ai-in-line-control'], {
        queryParams: { q: 'inLineThreadId:' + this.step.thread.id },
        queryParamsHandling: '',
      });
    } else {
      // Use side panel service to open findings thread as nested panel
      const sourcePipeline = this.inline() === 'true' ? 'inline' : undefined;
      this._sidePanel.openFindingThreadNested(this.step.thread.id, sourcePipeline);
    }
  }

  public performAttachmentAction($event: MenuAction, attachment: AttachedDocument) {
    switch ($event) {
      case this.downloadAttachmentAction:
        this._downloadAttachment(attachment);
        return;
      case this.deleteAttachmentAction:
        this._deleteAttachment(attachment);
        return;
    }
  }

  private _isInventoryEnrichmentEnabled(): boolean {
    return this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.DrInventoryEnrichment);
  }

  /**
   * Checks if the agent should be clickable
   * Returns true if inventoryId exists or for services with valid resourceId
   */
  public shouldShowAgentLink(aiService: AiService): boolean {
    return this._agentInventoryNavigationService.shouldShowAgentLink({
      inventoryId: this.step?.botDetails?.inventoryId,
      resourceId: this.step?.botDetails?.platformInfo?.resourceId,
      service: aiService,
    });
  }

  /**
   * Navigates to the agent in inventory or opens inventory drawer if inventoryId exists
   */
  public navigateToAgent(): void {
    if (!this.step) return;

    this._agentInventoryNavigationService.navigateToAgent({
      inventoryId: this.step.botDetails?.inventoryId,
      resourceId: this.step.botDetails?.platformInfo?.resourceId,
      service: this.step.service,
      integration: this.integration(),
    });
  }

  public getAiServiceIconPath(): string {
    if (!this.step) return '';
    return getAiServiceLogoPath(this.step.service);
  }

  public getAgentHeaderDisplayName(): string {
    if (!this.step) {
      return '';
    }
    try {
      return getAiServiceDisplayName(this.step.service);
    } catch {
      return '';
    }
  }

  public isInlineView(): boolean {
    if (this._isUnifiedPipeline()) return false;
    const urlInline = this.inline() === 'true' || this._route.snapshot.queryParamMap.get('inline') === 'true';
    const stepInline = !!(this.step && (this.step as unknown as { _inlineMode?: boolean })._inlineMode === true);
    return stepInline || urlInline;
  }

  private async _handleThreadIdsResponse(findings: JsonHalCollection<FindingModel>): Promise<void> {
    this.findingsByThreadId = findings._embedded.items;
    const hasFindings = this.findingsByThreadId?.length > 0;

    this.threadCardFields = [
      {
        key: this._labelsProviderPipe.transform('aiAgentStepPage.threadIdFieldName'),
        value: this.stepThreadId(),
        copyableText: this.stepThreadId(),
        isCopyable: true,
      },
      {
        key: this._labelsProviderPipe.transform('aiAgentStepPage.findingsFieldName'),
        customTemplate: hasFindings ? this.findingsTemplate() : undefined,
        value: hasFindings ? undefined : this._labelsProviderPipe.transform('aiAgentStepPage.noFindingsFound'),
      },
    ];

    // Add agent field if step is available
    if (this.step) {
      const isCustomM365Agent = isM365CustomAgent(this.step.service, this.step.botDetails.displayName);
      const agentField: ICardField = {
        key: this._labelsProviderPipe.transform('aiAgentStepPage.agentDetailsFieldName'),
      };

      // Use custom template for custom M365 agents, regular display for others
      if (isCustomM365Agent) {
        agentField.customTemplate = this.customAgentTemplate();
      } else {
        agentField.value = this._displayCamelCaseStringPipe.transform(this.step.botDetails.displayName);
        // Only add navigation method for agents that should be linkable
        if (this.shouldShowAgentLink(this.step.service as AiService)) {
          agentField.method = () => this.navigateToAgent();
        }
      }

      this.threadCardFields.push(agentField);
    }

    if (this.step?.environment) {
      this.threadCardFields.push({
        key: this._labelsProviderPipe.transform('aiAgentStepPage.environmentNameFieldName'),
        value: this.step.environment.displayName,
        copyableText: this.step.environment.displayName,
        isCopyable: true,
      });
    }
  }

  private _deleteAttachment(deletedAttachment: AttachedDocument) {
    this._aiAgentStepsService
      .deleteAiAgentStepAttachment(deletedAttachment.id, this.integration(), this.agentStep())
      .pipe(
        tap(() => {
          this.attachments.update((attachments) =>
            attachments.filter((attachment) => attachment !== deletedAttachment)
          );
          if (!this.attachments().length) {
            this.stepDetailsCardFields = this.stepDetailsCardFields.filter(
              (field) => field !== this.attachmentCardField()
            );
          }
          this._notificationService.success(Label.get('aiAgentStepPage.attachmentDeletedSuccessfully'));
        }),
        catchError(() => {
          this._notificationService.error(Label.get('aiAgentStepPage.attachmentDeletionFailed'));
          return EMPTY;
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  private _downloadAttachment(attachment: AttachedDocument) {
    this._aiAgentStepsService
      .downloadAiAgentStepAttachment(attachment.id, attachment.displayName, this.integration(), this.agentStep())
      .pipe(
        tap((response) => {
          const anchor: HTMLAnchorElement = this._renderer.createElement('a');
          anchor.href = response.fileUrl;
          anchor.download = attachment.displayName;
          anchor.target = '_blank';
          anchor.click();
          this._notificationService.success(Label.get('aiAgentStepPage.attachmentDownloadedSuccessfully'));
        }),
        catchError(() => {
          this._notificationService.error(Label.get('aiAgentStepPage.attachmentDownloadFailed'));
          return EMPTY;
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  private _isUnifiedPipeline(): boolean {
    return this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.UnifiedPipeline);
  }

  private _hasInlineTranscript(): boolean {
    const viewModel = this.viewModel();
    const inlineStepData = this._inlineStepData();
    let inlineTranscript: string | undefined = undefined;

    // For tool invocation request/result steps, check params/result fields
    const stepType = this.step?.type;

    if (stepType === StepType.toolInvocationRequestStep) {
      if (inlineStepData?.params !== undefined && inlineStepData.params !== '') {
        inlineTranscript = inlineStepData.params;
      } else if (viewModel && 'params' in viewModel) {
        inlineTranscript = (viewModel as { params?: string }).params;
      }
    } else if (stepType === StepType.toolInvocationResultStep) {
      if (inlineStepData?.result !== undefined && inlineStepData.result !== '') {
        inlineTranscript = inlineStepData.result;
      } else if (viewModel && 'result' in viewModel) {
        inlineTranscript = (viewModel as { result?: string }).result;
      }
    }

    if (inlineTranscript === undefined) {
      if (inlineStepData?.transcript !== undefined && inlineStepData.transcript !== '') {
        inlineTranscript = inlineStepData.transcript;
      } else if (viewModel && 'transcript' in viewModel) {
        inlineTranscript = (viewModel as { transcript?: string }).transcript;
      } else if (this.step && '_inlineTranscript' in this.step) {
        inlineTranscript = (this.step as { _inlineTranscript?: string })._inlineTranscript;
      }
    }

    return inlineTranscript !== undefined && inlineTranscript?.trim() !== '';
  }

  private _buildStepDetailsCard(step: AgentStepModel) {
    const isInline = this.isInlineView();
    this.stepDetailsCardFields = [];
    const citedSources: CitedSource[] = [];

    // Handle transcript field for message steps and agent handoff steps
    if (
      step.type === StepType.userMessageStep ||
      step.type === StepType.aiMessageStep ||
      step.type === StepType.memoryStep ||
      step.type === StepType.agentHandoffRequestStep ||
      step.type === StepType.agentHandoffResponseStep
    ) {
      const shouldShowFetchButton = !isInline || this._hasInlineTranscript();

      if (shouldShowFetchButton) {
        this.stepDetailsCardFields.push({
          key: this._labelsProviderPipe.transform('aiAgentStepPage.messageFieldName'),
          value: '',
          showPopupBtn: true,
          sensitiveBtnText: this._labelsProviderPipe.transform('aiAgentStepPage.showPromptContentLinkName'),
          isRawValue: true,
          sensitiveDataMethod: () =>
            this._isAnyDemoMockStep()
              ? this._fetchRsaMockSensitiveData(this.agentStep())
              : isInline
                ? this._searchActivityHttpService.findInLineActivityStepByIdViaQuery(this.agentStep()).pipe(
                    map((inlineStep: InLineActivityStepModel) => ({
                      resultCode: 'OK',
                      title: 'Transcript',
                      transcript: inlineStep?.transcript ?? '',
                    }))
                  )
                : this._aiAgentStepsService.getAgentStepSensitiveData(
                    this.integration(),
                    this.agentStep(),
                    SensitiveDataRequestActions.transcript
                  ),
        });
      }

      // Handle attachments for ChatGPT Enterprise
      if (step.service === AiService.chatGptEnterprise) {
        this.attachmentActions = [this.downloadAttachmentAction, this.deleteAttachmentAction];
      } else {
        this.attachmentActions = [];
      }

      if ('isTranscriptFetchingAllowed' in step && step.isTranscriptFetchingAllowed != null) {
        this.stepDetailsCardFields.push({
          key: this._labelsProviderPipe.transform('aiAgentStepPage.transcriptFetchingAllowedFieldName'),
          value: step.isTranscriptFetchingAllowed ? 'Yes' : 'No',
          tooltipLabelPath: 'aiAgentStepPage.transcriptFetchingAllowedFieldDescription',
          tooltipIconPath: 'assets/images/common/info.svg',
        });
      }

      if ('attachedDocuments' in step && step.attachedDocuments?.length) {
        this.attachments.set(step.attachedDocuments);
        this.stepDetailsCardFields.push(this.attachmentCardField());
      }
    }

    // Only handle other step types in regular mode (not supported in inline)
    if (!isInline) {
      switch (this.step.type) {
        case StepType.ragStep:
          this._handleRagStep(citedSources);
          break;
        case StepType.triggerStep:
          this._handleTriggerStep();
          break;
      }
    }

    // Tool invocation is supported in both modes
    if (this.step.type === StepType.toolInvocationStep) {
      if (!isInline || this._hasInlineTranscript()) {
        this._handleToolInvocationStep();
      }
    }

    // Tool request/response step types - two variants exist for backward compatibility:
    // - toolInvocationRequestStep/toolInvocationResultStep: Original types for inline activity pipeline
    // - toolRequestStep/toolResponseStep: New types for unified pipe (audit events pipeline)
    // Both pairs have identical UI behavior but different API values

    // Original tool request step (inline activity pipeline)
    if (this.step.type === StepType.toolInvocationRequestStep) {
      if (!isInline || this._hasInlineTranscript()) {
        this._handleToolInvocationRequestStep();
      }
    }

    // New tool request step (unified pipe / audit events pipeline)
    if (this.step.type === StepType.toolRequestStep) {
      if (!isInline || this._hasInlineTranscript()) {
        this._handleToolRequestStep();
      }
    }

    // Original tool result step (inline activity pipeline)
    if (this.step.type === StepType.toolInvocationResultStep) {
      if (!isInline || this._hasInlineTranscript()) {
        this._handleToolInvocationResultStep();
      }
    }

    // New tool response step (unified pipe / audit events pipeline)
    if (this.step.type === StepType.toolResponseStep) {
      if (!isInline || this._hasInlineTranscript()) {
        this._handleToolResponseStep();
      }
    }

    // Agent handoff steps are supported in both modes
    if (this.step.type === StepType.agentHandoffRequestStep) {
      if (!isInline || this._hasInlineTranscript()) {
        this._handleAgentHandoffRequestStep();
      }
    }

    if (this.step.type === StepType.agentHandoffResponseStep) {
      if (!isInline || this._hasInlineTranscript()) {
        this._handleAgentHandoffResponseStep();
      }
    }

    this.citedSources.set(citedSources);
  }

  // Handle RAG step (only in regular mode)
  private _handleRagStep(citedSources: CitedSource[]) {
    const ragStep = this.step as RagStep;

    switch (this.step.service) {
      case AiService.m365Copilot:
        for (const source of ragStep.citedSources) {
          const resourceType = resolveCitationResourceType(source, this.step.service);
          const m365Source = source as M365CopilotSource;
          citedSources.push({
            id: source.id,
            fileName: source.name,
            link: source.url ?? '',
            sensitivityLabelId: m365Source?.sensitivityLabelId,
            sensitivityLabel: m365Source?.sensitivityLabel,
            resourceType,
          });
        }
        break;
      case AiService.copilotStudio:
        const copilotRagStep = ragStep as CopilotStudioRagStep;
        if (copilotRagStep.ragQuery) {
          this.stepDetailsCardFields.push({
            key: this._labelsProviderPipe.transform('aiAgentStepPage.ragQuery'),
            value: copilotRagStep.ragQuery,
            sensitiveBtnText: this._labelsProviderPipe.transform('aiAgentStepPage.ragQuery'),
            isRawValue: true,
            showPopupBtn: false,
          });
        }
        if (copilotRagStep.sensitivityLabel?.name) {
          this.stepDetailsCardFields.push({
            key: this._labelsProviderPipe.transform('aiAgentStepPage.sensitivityLabelFieldName'),
            value: copilotRagStep.sensitivityLabel.name,
            isCopyable: true,
            copyableText: copilotRagStep.sensitivityLabel.name,
          });
        }
        for (const source of copilotRagStep.citedSources) {
          const resourceType = resolveCitationResourceType(source, this.step.service);
          const m365Source = source as M365CopilotSource;
          citedSources.push({
            id: source.id,
            fileName: source.name,
            link: source.url ?? '',
            sensitivityLabelId: m365Source?.sensitivityLabelId,
            sensitivityLabel: m365Source?.sensitivityLabel,
            resourceType,
            sensitiveData: {
              buttonText: this._labelsProviderPipe.transform('aiAgentStepPage.fetchCitationSnippet'),
              isRawValue: true,
              showPopupBtn: true,
              sensitiveDataMethod: this._getSensitiveDataMethod(SensitiveDataRequestActions.snippet, source.id),
            },
          });
        }
        if (copilotRagStep.sources) {
          for (const source of copilotRagStep.sources) {
            const resourceType = resolveCitationResourceType(source, this.step.service);
            const m365Source = source as M365CopilotSource;
            this.sources.update((sources) => [
              ...sources,
              {
                id: source.id,
                fileName: source.name ?? source.id,
                resourceType,
                link: source.url ?? '',
                sensitivityLabelId: m365Source?.sensitivityLabelId,
                sensitivityLabel: m365Source?.sensitivityLabel,
              },
            ]);
          }
        }
        break;
      case AiService.chatGptEnterprise:
      case AiService.azureAiFoundry:
        for (const source of ragStep.citedSources) {
          const resourceType = resolveCitationResourceType(source, this.step.service);
          citedSources.push({
            id: source.id,
            fileName: source.name,
            link: source.url ?? '',
            resourceType,
          });
        }
        break;
      // vertexAi specific extras
      case AiService.vertexAi: {
        const vertexRagStep = ragStep as VertexAiRagStep;
        if (vertexRagStep.ragQueryAvailable) {
          this.stepDetailsCardFields.push({
            key: this._labelsProviderPipe.transform('aiAgentStepPage.ragQuery'),
            value: '',
            sensitiveBtnText: this._labelsProviderPipe.transform('aiAgentStepPage.ragQuery'),
            isRawValue: true,
            showPopupBtn: true,
            sensitiveDataMethod: this._getSensitiveDataMethod(SensitiveDataRequestActions.ragQuery),
          });
        }

        for (const source of vertexRagStep.citedSources) {
          const resourceType = resolveCitationResourceType(source, this.step.service);
          const citedSource: CitedSource = {
            id: source.id,
            fileName: source.name,
            link: source.url ?? '',
            name: source.name,
            resourceType,
          };

          if (source.contentAvailable) {
            citedSource.sensitiveData = {
              buttonText: this._labelsProviderPipe.transform('aiAgentStepPage.fetchCitationSnippet'),
              isRawValue: true,
              showPopupBtn: true,
              sensitiveDataMethod: this._getSensitiveDataMethod(SensitiveDataRequestActions.snippet, source.id),
            };
          }

          citedSources.push(citedSource);
        }
        break;
      }
      case AiService.agentforce: {
        const agentforceRagStep = ragStep as AgentforceRagStep;
        // Show RAG query if available
        if (agentforceRagStep.ragQuery) {
          this.stepDetailsCardFields.push({
            key: this._labelsProviderPipe.transform('aiAgentStepPage.ragQuery'),
            value: agentforceRagStep.ragQuery,
            sensitiveBtnText: this._labelsProviderPipe.transform('aiAgentStepPage.ragQuery'),
            isRawValue: true,
            showPopupBtn: false,
          });
        }

        // Show cited sources (no snippet fetching support for Agentforce)
        for (const source of agentforceRagStep.citedSources) {
          const resourceType = resolveCitationResourceType(source, this.step.service);
          citedSources.push({
            id: source.id,
            fileName: source.name,
            link: source.url ?? '',
            resourceType,
          });
        }
        break;
      }
    }
  }

  // Handle trigger step (only in regular mode)
  private _handleTriggerStep() {
    if (this.step.service === AiService.copilotStudio) {
      const triggerStep = this.step as CopilotStudioTriggerStep;
      this._addSensitiveDataFieldWithConfirmation(
        'aiAgentStepPage.fetchPayload',
        'aiAgentStepPage.fetchPayload',
        SensitiveDataRequestActions.payload,
        'aiAgentStepPayloadDialog'
      );

      if (triggerStep.triggerDisplayName) {
        this.stepDetailsCardFields.push({
          key: this._labelsProviderPipe.transform('aiAgentStepPage.triggeringEvent'),
          value: triggerStep.triggerDisplayName,
          iconPath: triggerStep.triggerIconUrl,
        });
      }
    }
  }

  // Helper: Add tool display name field with icon support and inventory enrichment
  private _addToolDisplayNameField(toolDisplayName: string, step: AgentStepModel, toolId?: string): void {
    const field: ICardField = {
      key: this._labelsProviderPipe.transform('aiAgentStepPage.toolField'),
      value: toolDisplayName,
    };

    // Add icon for CopilotStudio
    if (step.service === AiService.copilotStudio && 'triggerIconUrl' in step) {
      const stepWithIcon = step as { triggerIconUrl?: string };
      if (stepWithIcon.triggerIconUrl) {
        field.iconPath = stepWithIcon.triggerIconUrl;
      }
    }

    // Add click handler if tool has inventoryId and feature is enabled
    if (this._isInventoryEnrichmentEnabled() && toolId != null && toolId.trim() !== '') {
      field.method = () => {
        this._sidePanel.openResourceDetailsFromIssue(toolId, this.integration());
      };
    }

    this.stepDetailsCardFields.push(field);
  }

  // Helper: Add tool origin field
  private _addToolOriginField(toolOrigin: string): void {
    this.stepDetailsCardFields.push({
      key: this._labelsProviderPipe.transform('aiAgentStepPage.toolOriginFieldName'),
      value: toolOrigin,
    });
  }

  // Helper: Add tool invocation fields (display name and origin)
  private _addToolInvocationFields(
    toolInvocations: { toolDisplayName?: string; toolOrigin?: string; toolId?: string }[]
  ): void {
    if (toolInvocations.length > 0) {
      const toolInvocation = toolInvocations[0];

      if (toolInvocation.toolDisplayName) {
        this._addToolDisplayNameField(toolInvocation.toolDisplayName, this.step, toolInvocation.toolId);
      }

      if (toolInvocation.toolOrigin) {
        this._addToolOriginField(toolInvocation.toolOrigin);
      }
    }
  }

  // Handle tool invocation result step - ORIGINAL type for inline activity pipeline
  // This is the counterpart to _handleToolResponseStep (unified pipe / audit events pipeline)
  // Both handlers have identical UI behavior but handle different API step types
  private _handleToolInvocationResultStep() {
    const toolResultStep = this.step as ToolInvocationResultStep;
    const isInline = this.isInlineView();

    // Add tool invocation result field with inline fetch support
    this._addSensitiveDataFieldForToolInvocation(
      'aiAgentStepPage.toolInvocationResult',
      'aiAgentStepPage.fetchToolInvocationResult',
      'aiAgentStepToolInvocationResultDialog',
      SensitiveDataRequestActions.invocationResult
    );

    // Add link to related tool call request step if executionId is available
    if (toolResultStep.executionId) {
      this.stepDetailsCardFields.push({
        key: this._labelsProviderPipe.transform('aiAgentStepPage.viewRelatedToolCallRequest'),
        value: this._labelsProviderPipe.transform('aiAgentStepPage.viewRelatedToolCallRequest'),
        method: () => this._navigateToRelatedToolCallRequestStep(toolResultStep.executionId!),
      });
    } else if (isInline) {
      const inlineStepData = this._inlineStepData();
      if (inlineStepData?.result) {
        try {
          const resultObj = JSON.parse(inlineStepData.result);
          if (resultObj?.executionId) {
            this.stepDetailsCardFields.push({
              key: this._labelsProviderPipe.transform('aiAgentStepPage.viewRelatedToolCallRequest'),
              value: this._labelsProviderPipe.transform('aiAgentStepPage.viewRelatedToolCallRequest'),
              method: () => this._navigateToRelatedToolCallRequestStep(resultObj.executionId),
            });
          }
        } catch {
          // If parsing fails, result is not JSON, skip the link
        }
      }
    }

    if (toolResultStep.toolInvocations && toolResultStep.toolInvocations.length > 0) {
      this._addToolInvocationFields(toolResultStep.toolInvocations);
    }

    // Add invocation details for ChatGPT Enterprise (only in regular mode)
    if (this.step.service === AiService.chatGptEnterprise) {
      const chatGptToolStep = toolResultStep as ChatGptEnterpriseToolInvocationResultStep;
      if (
        chatGptToolStep.invocationsDetails &&
        chatGptToolStep.invocationsDetails.length > 0 &&
        chatGptToolStep.invocationsDetails[0].operationSchema &&
        chatGptToolStep.invocationsDetails[0].server &&
        chatGptToolStep.invocationsDetails[0].method &&
        chatGptToolStep.invocationsDetails[0].path
      ) {
        const details = chatGptToolStep.invocationsDetails[0];
        const operationSchemaValue = (() => {
          try {
            if (details.operationSchema) {
              return JSON.stringify(JSON.parse(details.operationSchema), null, 4);
            }
            return '';
          } catch {
            return details.operationSchema || '';
          }
        })();

        this.stepDetailsCardFields.push(
          {
            key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationsDetailsServerFieldName'),
            value: details.server,
          },
          {
            key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationsDetailsPathFieldName'),
            value: details.path,
          },
          {
            key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationsDetailsMethodFieldName'),
            value: details.method,
          },
          {
            key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationsDetailsOperationFieldName'),
            value: operationSchemaValue,
            isRawValue: true,
          }
        );
      }
    }
  }

  // Navigate to related tool call request step by executionId
  private _navigateToRelatedToolCallRequestStep(executionId: string): void {
    const isInline = this.isInlineView();

    if (isInline) {
      // For inline mode, search for the step with matching executionId in params
      // Since we don't have a direct API to search by executionId, we'll navigate to activity page with a search query
      // The backend would need to support searching by executionId in the params JSON
      this._router.navigate(['app/ai-activity'], {
        queryParams: {
          inline: 'true',
          q: `stepType:toolCallRequestStep executionId:"${executionId}"`,
        },
      });
    } else {
      // For regular mode, we would need to search through related steps or use a similar approach
      // This would require backend support to find steps by executionId
      this._notificationService.info('Finding related tool call request step...');
      // TODO: Implement backend API call to find step by executionId
    }
  }

  // Helper: Create sensitive data field for tool invocations with inline/regular mode support
  private _addSensitiveDataFieldForToolInvocation(
    keyLabel: string,
    buttonLabel: string,
    dialogPrefix: string,
    action: SensitiveDataRequestActions
  ): void {
    const isInline = this.isInlineView();

    this.stepDetailsCardFields.push({
      key: this._labelsProviderPipe.transform(keyLabel),
      value: '',
      confirmedData: isInline
        ? undefined
        : {
            title: Label.get(`${dialogPrefix}.confirmationDialogTitle`),
            content: Label.get(`${dialogPrefix}.confirmationDialogDescription`),
            positiveOptionText: Label.get(`${dialogPrefix}.confirmationDialogYes`),
            negativeOptionText: Label.get(`${dialogPrefix}.confirmationDialogNo`),
          },
      isRawValue: true,
      sensitiveBtnText: this._labelsProviderPipe.transform(buttonLabel),
      showPopupBtn: true,
      sensitiveDataMethod: this._getSensitiveDataMethod(action),
    });
  }

  // Handle tool invocation step (supported in both modes)
  private _handleToolInvocationStep() {
    const toolStep = this.step as ToolInvocationStep;
    const isInline = this.isInlineView();

    // Add invocation parameters field with inline fetch support
    this.stepDetailsCardFields.push({
      key: this._labelsProviderPipe.transform('aiAgentStepPage.invocationParameters'),
      value: '',
      confirmedData: isInline
        ? undefined
        : {
            title: Label.get('aiAgentStepToolInvocationParamsDialog.confirmationDialogTitle'),
            content: Label.get('aiAgentStepToolInvocationParamsDialog.confirmationDialogDescription'),
            positiveOptionText: Label.get('aiAgentStepToolInvocationParamsDialog.confirmationDialogYes'),
            negativeOptionText: Label.get('aiAgentStepToolInvocationParamsDialog.confirmationDialogNo'),
          },
      isRawValue: true,
      sensitiveBtnText: this._labelsProviderPipe.transform('aiAgentStepPage.fetchToolInvocationParams'),
      showPopupBtn: true,
      sensitiveDataMethod: this._getSensitiveDataMethod(SensitiveDataRequestActions.invocationParams),
    });

    // Add tool fields if available
    if (toolStep.toolInvocations.length > 0) {
      const toolInvocation = toolStep.toolInvocations[0];

      if (toolInvocation.toolDisplayName) {
        const field: ICardField = {
          key: this._labelsProviderPipe.transform('aiAgentStepPage.toolField'),
          value: toolInvocation.toolDisplayName,
        };

        // Add icon for CopilotStudio
        if (this.step.service === AiService.copilotStudio && 'triggerIconUrl' in toolStep) {
          const copilotToolStep = toolStep as CopilotStudioToolInvocationStep;
          if (copilotToolStep.triggerIconUrl) {
            field.iconPath = copilotToolStep.triggerIconUrl;
          }
        }

        // Add click handler if tool has inventoryId and feature is enabled
        const toolInventoryId = toolInvocation.toolId;
        if (this._isInventoryEnrichmentEnabled() && toolInventoryId != null && toolInventoryId.trim() !== '') {
          field.method = () => {
            this._sidePanel.openResourceDetailsFromIssue(toolInventoryId, this.integration());
          };
        }

        this.stepDetailsCardFields.push(field);
      }

      if (toolInvocation.toolOrigin) {
        this.stepDetailsCardFields.push({
          key: this._labelsProviderPipe.transform('aiAgentStepPage.toolOriginFieldName'),
          value: toolInvocation.toolOrigin,
        });
      }
    }

    // Add invocation result field with inline fetch support
    this.stepDetailsCardFields.push({
      key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationResult'),
      value: '',
      confirmedData: isInline
        ? undefined
        : {
            title: Label.get('aiAgentStepToolInvocationResultDialog.confirmationDialogTitle'),
            content: Label.get('aiAgentStepToolInvocationResultDialog.confirmationDialogDescription'),
            positiveOptionText: Label.get('aiAgentStepToolInvocationResultDialog.confirmationDialogYes'),
            negativeOptionText: Label.get('aiAgentStepToolInvocationResultDialog.confirmationDialogNo'),
          },
      isRawValue: true,
      sensitiveBtnText: this._labelsProviderPipe.transform('aiAgentStepPage.fetchToolInvocationResult'),
      showPopupBtn: true,
      sensitiveDataMethod: this._getSensitiveDataMethod(SensitiveDataRequestActions.invocationResult),
    });

    // Add invocation details for ChatGPT Enterprise (only in regular mode)
    if (this.step.service === AiService.chatGptEnterprise) {
      const chatGptToolStep = toolStep as ChatGptEnterpriseToolInvocationStep;
      if (
        chatGptToolStep.invocationsDetails &&
        chatGptToolStep.invocationsDetails.length > 0 &&
        chatGptToolStep.invocationsDetails[0].operationSchema &&
        chatGptToolStep.invocationsDetails[0].server &&
        chatGptToolStep.invocationsDetails[0].method &&
        chatGptToolStep.invocationsDetails[0].path
      ) {
        const details = chatGptToolStep.invocationsDetails[0];
        const operationSchemaValue = (() => {
          try {
            if (details.operationSchema) {
              return JSON.stringify(JSON.parse(details.operationSchema), null, 4);
            }
            return '';
          } catch {
            return details.operationSchema || '';
          }
        })();

        this.stepDetailsCardFields.push(
          {
            key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationsDetailsServerFieldName'),
            value: details.server,
          },
          {
            key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationsDetailsPathFieldName'),
            value: details.path,
          },
          {
            key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationsDetailsMethodFieldName'),
            value: details.method,
          },
          {
            key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationsDetailsOperationFieldName'),
            value: operationSchemaValue,
            isRawValue: true,
          }
        );
      }
    }
  }

  // Handle tool invocation request step - ORIGINAL type for inline activity pipeline
  // This is the counterpart to _handleToolRequestStep (unified pipe / audit events pipeline)
  // Both handlers have identical UI behavior but handle different API step types
  private _handleToolInvocationRequestStep() {
    const toolStep = this.step as ToolInvocationRequestStep;
    const isInline = this.isInlineView();

    // Add invocation parameters field with inline fetch support
    this.stepDetailsCardFields.push({
      key: this._labelsProviderPipe.transform('aiAgentStepPage.invocationParameters'),
      value: '',
      confirmedData: isInline
        ? undefined
        : {
            title: Label.get('aiAgentStepToolInvocationParamsDialog.confirmationDialogTitle'),
            content: Label.get('aiAgentStepToolInvocationParamsDialog.confirmationDialogDescription'),
            positiveOptionText: Label.get('aiAgentStepToolInvocationParamsDialog.confirmationDialogYes'),
            negativeOptionText: Label.get('aiAgentStepToolInvocationParamsDialog.confirmationDialogNo'),
          },
      isRawValue: true,
      sensitiveBtnText: this._labelsProviderPipe.transform('aiAgentStepPage.fetchToolInvocationParams'),
      showPopupBtn: true,
      sensitiveDataMethod: this._getSensitiveDataMethod(SensitiveDataRequestActions.invocationParams),
    });

    // Add tool fields if available
    if (toolStep.toolInvocations?.length > 0) {
      this._addToolInvocationFields(toolStep.toolInvocations);
    }

    // Add invocation details for ChatGPT Enterprise (only in regular mode)
    if (this.step.service === AiService.chatGptEnterprise) {
      const chatGptToolStep = toolStep as ChatGptEnterpriseToolInvocationRequestStep;
      if (
        chatGptToolStep.invocationsDetails &&
        chatGptToolStep.invocationsDetails.length > 0 &&
        chatGptToolStep.invocationsDetails[0].operationSchema &&
        chatGptToolStep.invocationsDetails[0].server &&
        chatGptToolStep.invocationsDetails[0].method &&
        chatGptToolStep.invocationsDetails[0].path
      ) {
        const details = chatGptToolStep.invocationsDetails[0];
        const operationSchemaValue = (() => {
          try {
            if (details.operationSchema) {
              return JSON.stringify(JSON.parse(details.operationSchema), null, 4);
            }
            return '';
          } catch {
            return details.operationSchema || '';
          }
        })();

        this.stepDetailsCardFields.push(
          {
            key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationsDetailsServerFieldName'),
            value: details.server,
          },
          {
            key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationsDetailsPathFieldName'),
            value: details.path,
          },
          {
            key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationsDetailsMethodFieldName'),
            value: details.method,
          },
          {
            key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationsDetailsOperationFieldName'),
            value: operationSchemaValue,
            isRawValue: true,
          }
        );
      }
    }
  }

  // Handle tool request step - NEW type for unified pipe (audit events pipeline)
  // This is the counterpart to _handleToolInvocationRequestStep (inline activity pipeline)
  // Both handlers have identical UI behavior but handle different API step types
  private _handleToolRequestStep() {
    const toolStep = this.step as ToolRequestStep;
    const isInline = this.isInlineView();

    // Add invocation parameters field with inline fetch support
    this.stepDetailsCardFields.push({
      key: this._labelsProviderPipe.transform('aiAgentStepPage.invocationParameters'),
      value: '',
      confirmedData: isInline
        ? undefined
        : {
            title: Label.get('aiAgentStepToolInvocationParamsDialog.confirmationDialogTitle'),
            content: Label.get('aiAgentStepToolInvocationParamsDialog.confirmationDialogDescription'),
            positiveOptionText: Label.get('aiAgentStepToolInvocationParamsDialog.confirmationDialogYes'),
            negativeOptionText: Label.get('aiAgentStepToolInvocationParamsDialog.confirmationDialogNo'),
          },
      isRawValue: true,
      sensitiveBtnText: this._labelsProviderPipe.transform('aiAgentStepPage.fetchToolInvocationParams'),
      showPopupBtn: true,
      sensitiveDataMethod: this._getSensitiveDataMethod(SensitiveDataRequestActions.invocationParams),
    });

    // Add tool fields if available (using toolInvocationRequests array)
    if (toolStep.toolInvocationRequests?.length > 0) {
      this._addToolInvocationFields(toolStep.toolInvocationRequests);
    }

    // Add invocation details for ChatGPT Enterprise
    if (this.step.service === AiService.chatGptEnterprise) {
      const chatGptToolStep = toolStep as ChatGptEnterpriseToolRequestStep;
      if (
        chatGptToolStep.invocationsDetails &&
        chatGptToolStep.invocationsDetails.length > 0 &&
        chatGptToolStep.invocationsDetails[0].operationSchema &&
        chatGptToolStep.invocationsDetails[0].server &&
        chatGptToolStep.invocationsDetails[0].method &&
        chatGptToolStep.invocationsDetails[0].path
      ) {
        const details = chatGptToolStep.invocationsDetails[0];
        const operationSchemaValue = (() => {
          try {
            if (details.operationSchema) {
              return JSON.stringify(JSON.parse(details.operationSchema), null, 4);
            }
            return '';
          } catch {
            return details.operationSchema || '';
          }
        })();

        this.stepDetailsCardFields.push(
          {
            key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationsDetailsServerFieldName'),
            value: details.server,
          },
          {
            key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationsDetailsPathFieldName'),
            value: details.path,
          },
          {
            key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationsDetailsMethodFieldName'),
            value: details.method,
          },
          {
            key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationsDetailsOperationFieldName'),
            value: operationSchemaValue,
            isRawValue: true,
          }
        );
      }
    }
  }

  // Handle tool response step - NEW type for unified pipe (audit events pipeline)
  // This is the counterpart to _handleToolInvocationResultStep (inline activity pipeline)
  // Both handlers have identical UI behavior but handle different API step types
  private _handleToolResponseStep() {
    const toolResponseStep = this.step as ToolResponseStep;
    const isInline = this.isInlineView();

    // Add tool invocation result field with inline fetch support
    this._addSensitiveDataFieldForToolInvocation(
      'aiAgentStepPage.toolInvocationResult',
      'aiAgentStepPage.fetchToolInvocationResult',
      'aiAgentStepToolInvocationResultDialog',
      SensitiveDataRequestActions.invocationResult
    );

    // Add link to related tool request step if executionId is available
    if (toolResponseStep.executionId) {
      this.stepDetailsCardFields.push({
        key: this._labelsProviderPipe.transform('aiAgentStepPage.viewRelatedToolCallRequest'),
        value: this._labelsProviderPipe.transform('aiAgentStepPage.viewRelatedToolCallRequest'),
        method: () => this._navigateToRelatedToolRequestStep(toolResponseStep.executionId!),
      });
    } else if (isInline) {
      const inlineStepData = this._inlineStepData();
      if (inlineStepData?.result) {
        try {
          const resultObj = JSON.parse(inlineStepData.result);
          if (resultObj?.executionId) {
            this.stepDetailsCardFields.push({
              key: this._labelsProviderPipe.transform('aiAgentStepPage.viewRelatedToolCallRequest'),
              value: this._labelsProviderPipe.transform('aiAgentStepPage.viewRelatedToolCallRequest'),
              method: () => this._navigateToRelatedToolRequestStep(resultObj.executionId),
            });
          }
        } catch {
          // If parsing fails, result is not JSON, skip the link
        }
      }
    }

    // Add tool fields if available (using toolInvocationResponses array)
    if (toolResponseStep.toolInvocationResponses && toolResponseStep.toolInvocationResponses.length > 0) {
      this._addToolInvocationFields(toolResponseStep.toolInvocationResponses);
    }

    // Add invocation details for ChatGPT Enterprise
    if (this.step.service === AiService.chatGptEnterprise) {
      const chatGptToolStep = toolResponseStep as ChatGptEnterpriseToolResponseStep;
      if (
        chatGptToolStep.invocationsDetails &&
        chatGptToolStep.invocationsDetails.length > 0 &&
        chatGptToolStep.invocationsDetails[0].operationSchema &&
        chatGptToolStep.invocationsDetails[0].server &&
        chatGptToolStep.invocationsDetails[0].method &&
        chatGptToolStep.invocationsDetails[0].path
      ) {
        const details = chatGptToolStep.invocationsDetails[0];
        const operationSchemaValue = (() => {
          try {
            if (details.operationSchema) {
              return JSON.stringify(JSON.parse(details.operationSchema), null, 4);
            }
            return '';
          } catch {
            return details.operationSchema || '';
          }
        })();

        this.stepDetailsCardFields.push(
          {
            key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationsDetailsServerFieldName'),
            value: details.server,
          },
          {
            key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationsDetailsPathFieldName'),
            value: details.path,
          },
          {
            key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationsDetailsMethodFieldName'),
            value: details.method,
          },
          {
            key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationsDetailsOperationFieldName'),
            value: operationSchemaValue,
            isRawValue: true,
          }
        );
      }
    }
  }

  // Navigate to related tool request step by executionId
  private _navigateToRelatedToolRequestStep(executionId: string): void {
    const isInline = this.isInlineView();

    if (isInline) {
      this._router.navigate(['app/ai-activity'], {
        queryParams: {
          inline: 'true',
          q: `stepType:toolInvocationRequestStep executionId:"${executionId}"`,
        },
      });
    } else {
      this._notificationService.info('Finding related tool request step...');
    }
  }

  // Handle agent handoff request step
  private _handleAgentHandoffRequestStep() {
    const handoffStep = this.step as AgentHandoffRequestStep;
    this._addA2ACommunicationFields(handoffStep, 'request');
  }

  // Handle agent handoff response step
  private _handleAgentHandoffResponseStep() {
    const handoffStep = this.step as AgentHandoffResponseStep;
    this._addA2ACommunicationFields(handoffStep, 'response');
  }

  // Add A2A communication fields for agent handoff steps
  private _addA2ACommunicationFields(
    handoffStep: AgentHandoffRequestStep | AgentHandoffResponseStep,
    stepType: 'request' | 'response'
  ) {
    this.a2aCommunicationCardFields = [];

    // Add requesting agent field with navigation
    if (handoffStep.requestingAgent) {
      const requestingField: ICardField = {
        key: this._labelsProviderPipe.transform('aiAgentStepPage.requestingAgentFieldName'),
        value: handoffStep.requestingAgent.displayName || 'Unknown',
      };

      // Add navigation if inventoryId exists
      if (this._shouldShowBotLink(handoffStep.requestingAgent)) {
        requestingField.method = () => this._navigateToBotInventory(handoffStep.requestingAgent!);
      }

      this.a2aCommunicationCardFields.push(requestingField);
    }

    // Add responding agent field with navigation
    if (handoffStep.respondingAgent) {
      const respondingField: ICardField = {
        key: this._labelsProviderPipe.transform('aiAgentStepPage.respondingAgentFieldName'),
        value: handoffStep.respondingAgent.displayName || 'Unknown',
      };

      // Add navigation if inventoryId exists
      if (this._shouldShowBotLink(handoffStep.respondingAgent)) {
        respondingField.method = () => this._navigateToBotInventory(handoffStep.respondingAgent!);
      }

      this.a2aCommunicationCardFields.push(respondingField);
    }

    // Add link to related step (sibling step)
    if (handoffStep.siblingStepId) {
      const relatedStepLabel =
        stepType === 'request'
          ? 'aiAgentStepPage.viewRelatedHandoffResponse'
          : 'aiAgentStepPage.viewRelatedHandoffRequest';
      this.a2aCommunicationCardFields.push({
        key: this._labelsProviderPipe.transform('aiAgentStepPage.relatedStepFieldName'),
        value: this._labelsProviderPipe.transform(relatedStepLabel),
        method: () => this._navigateToRelatedHandoffStep(handoffStep.siblingStepId!),
      });
    }
  }

  // Check if bot should be clickable based on inventoryId
  private _shouldShowBotLink(botDetails: BotDetails): boolean {
    return this._agentInventoryNavigationService.shouldShowAgentLink({
      inventoryId: botDetails.inventoryId,
      resourceId: botDetails.platformInfo?.resourceId,
      service: this.step?.service,
    });
  }

  // Navigate to bot in inventory
  private _navigateToBotInventory(botDetails: BotDetails): void {
    this._agentInventoryNavigationService.navigateToAgent({
      inventoryId: botDetails.inventoryId,
      resourceId: botDetails.platformInfo?.resourceId,
      service: this.step?.service,
      integration: this.integration(),
    });
  }

  // Navigate to related agent handoff step by siblingStepId
  private _navigateToRelatedHandoffStep(siblingStepId: string): void {
    const isInline = this.isInlineView();
    const queryParams: Record<string, string> = {
      stepThreadId: this.stepThreadId(),
      agentStep: siblingStepId,
      integration: this.integration(),
    };

    if (isInline) {
      queryParams['inline'] = 'true';
    }

    this._router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge',
    });
  }

  // Reusable helper for sensitive data fields with confirmation
  private _addSensitiveDataFieldWithConfirmation(
    keyLabel: string,
    buttonLabel: string,
    action: SensitiveDataRequestActions,
    dialogPrefix: string,
    sourceId?: string
  ) {
    this.stepDetailsCardFields.push({
      key: this._labelsProviderPipe.transform(keyLabel),
      value: '',
      confirmedData: {
        title: Label.get(`${dialogPrefix}.confirmationDialogTitle`),
        content: Label.get(`${dialogPrefix}.confirmationDialogDescription`),
        positiveOptionText: Label.get(`${dialogPrefix}.confirmationDialogYes`),
        negativeOptionText: Label.get(`${dialogPrefix}.confirmationDialogNo`),
      },
      isRawValue: true,
      sensitiveBtnText: this._labelsProviderPipe.transform(buttonLabel),
      showPopupBtn: true,
      sensitiveDataMethod: this._getSensitiveDataMethod(action, sourceId),
    });
  }

  private _buildActorCard(step: AgentStepModel) {
    if (this.isInlineView()) {
      if (!step.mainActor) {
        this.actorCardFieldList = [];
        return;
      }

      const actorNameField: ICardField = {
        key: this._labelsProviderPipe.transform('aiAgentStepPage.nameFieldName'),
        value: step.mainActor.displayName,
      };

      const actorInventoryId = step.mainActor.inventoryId;
      if (this._agentInventoryNavigationService.shouldShowActorLink({ actorInventoryId })) {
        actorNameField.method = () => {
          this._agentInventoryNavigationService.navigateToActor({
            actorInventoryId,
            integration: this.integration(),
          });
        };
      }

      this.actorCardFieldList = [[actorNameField]];
    } else {
      // If relatedActors is empty but mainActor exists, create a single actor entry from mainActor
      if (!this.step.relatedActors || this.step.relatedActors.length === 0) {
        if (!this.step.mainActor) {
          this.actorCardFieldList = [];
          return;
        }

        // Create actor card from mainActor
        const actorNameField: ICardField = {
          key: this._labelsProviderPipe.transform('aiAgentStepPage.nameFieldName'),
          value: this.step.mainActor.displayName,
        };

        const actorInventoryId = this.step.mainActor.inventoryId;
        if (this._agentInventoryNavigationService.shouldShowActorLink({ actorInventoryId })) {
          actorNameField.method = () => {
            this._agentInventoryNavigationService.navigateToActor({
              actorInventoryId,
              integration: this.integration(),
            });
          };
        }

        const fields: ICardField[] = [actorNameField];
        if (this.step.mainActor.email) {
          fields.push({
            key: this._labelsProviderPipe.transform('aiAgentStepPage.emailFieldName'),
            value: this.step.mainActor.email,
          });
        }

        this.actorCardFieldList = [fields];
        return;
      }

      this.actorCardFieldList = this.step.relatedActors.map((actor) => {
        const fields: ICardField[] = [];

        const useMainActor =
          (actor.inventoryId == null || actor.inventoryId.trim() === '') &&
          actor.id === this.step.mainActor?.id &&
          this.step.mainActor?.inventoryId != null &&
          this.step.mainActor.inventoryId.trim() !== '';

        const actorDisplayName = useMainActor ? this.step.mainActor.displayName : actor.displayName;
        const actorInventoryId = useMainActor ? this.step.mainActor.inventoryId : actor.inventoryId;

        const actorNameField: ICardField = {
          key: this._labelsProviderPipe.transform('aiAgentStepPage.nameFieldName'),
          value: actorDisplayName,
        };

        if (this._agentInventoryNavigationService.shouldShowActorLink({ actorInventoryId })) {
          actorNameField.method = () => {
            this._agentInventoryNavigationService.navigateToActor({
              actorInventoryId,
              integration: this.integration(),
            });
          };
        }

        fields.push(actorNameField);
        if (actor.email) {
          fields.push({
            key: this._labelsProviderPipe.transform('aiAgentStepPage.emailFieldName'),
            value: actor.email,
          });
        }
        return fields;
      });
    }
  }

  private _buildClientCard(step: AgentStepModel) {
    // Inline and regular share the same label now
    if ((step as unknown as { _inlineMode?: boolean })._inlineMode) {
      this.clientCardFields = [
        {
          key: this._labelsProviderPipe.transform('aiAgentStepPage.applicationFieldName'),
          value: step.clientContext.clientApplication,
        },
      ];
    } else {
      this.clientCardFields = [
        {
          key: this._labelsProviderPipe.transform('aiAgentStepPage.applicationFieldName'),
          value: this._displayCamelCaseStringPipe.transform(this.step.clientContext.clientApplication),
          iconPath: getClientApplicationIconPath(this.step.clientContext.clientApplication),
        },
      ];
      if (this.step.clientContext.region) {
        this.clientCardFields.push({
          key: this._labelsProviderPipe.transform('aiAgentStepPage.regionFieldName'),
          value: this.step.clientContext.region,
        });
      }
      if (this.step.clientContext.ipAddress) {
        this.clientCardFields.push({
          key: this._labelsProviderPipe.transform('aiAgentStepPage.ipAddressFieldName'),
          value: this.step.clientContext.ipAddress,
        });
      }
      if (this.step.clientContext.chatMode && this.step.clientContext.chatMode !== ChatMode.na) {
        this.clientCardFields.push({
          key: this._labelsProviderPipe.transform('aiAgentStepPage.chatModeFieldName'),
          value: this.step.clientContext.chatMode,
        });
      }
    }
  }

  private _buildThreadCard(step: AgentStepModel) {
    // Only show thread card if threadId exists and not empty/'null'
    if (!step.thread?.id || step.thread.id === 'null' || step.thread.id === '') {
      this.threadCardFields = [];
      this.showThreadSection = false;
      return;
    }

    this.showThreadSection = true;
    const hasFindings = this.findingsByThreadId?.length > 0;

    this.threadCardFields = [
      {
        key: this._labelsProviderPipe.transform('aiAgentStepPage.threadIdFieldName'),
        value: this.stepThreadId(),
        copyableText: this.stepThreadId(),
        isCopyable: true,
      },
      {
        key: this._labelsProviderPipe.transform('aiAgentStepPage.findingsFieldName'),
        customTemplate: hasFindings ? this.findingsTemplate() : undefined,
        value: hasFindings ? undefined : this._labelsProviderPipe.transform('aiAgentStepPage.noFindingsFound'),
      },
    ];

    // Add agent field if step is available
    if (this.step) {
      const isCustomM365Agent = isM365CustomAgent(this.step.service, this.step.botDetails.displayName);
      const agentField: ICardField = {
        key: this._labelsProviderPipe.transform('aiAgentStepPage.agentDetailsFieldName'),
      };

      // Use custom template for custom M365 agents, regular display for others
      if (isCustomM365Agent) {
        agentField.customTemplate = this.customAgentTemplate();
      } else {
        agentField.value = this._displayCamelCaseStringPipe.transform(this.step.botDetails.displayName);
        // Only add navigation method for agents that should be linkable
        if (this.shouldShowAgentLink(this.step.service as AiService)) {
          agentField.method = () => this.navigateToAgent();
        }
      }

      this.threadCardFields.push(agentField);
    }

    if (this.step?.environment) {
      this.threadCardFields.push({
        key: this._labelsProviderPipe.transform('aiAgentStepPage.environmentNameFieldName'),
        value: this.step.environment.displayName,
        copyableText: this.step.environment.displayName,
        isCopyable: true,
      });
    }
  }

  private _setMenuActions(step: AgentStepModel) {
    const isInline = this.isInlineView();
    if (isInline) {
      // Inline mode: no menu actions
      this.menuActions$.next([]);
      return;
    }
    const tempView: AiAgentStepActivityTableRowViewModel = {
      id: this.agentStep(),
      integrationId: this.integration(),
      actorName: '',
      timestamp: '',
      platformIcon: '',
      stepType: '',
      threadId: '',
      agentName: '',
      application: '',
      step: step,
      clientApplicationIcon: '',
      clientApplication: '',
      rowId: '',
      clickable: false,
    };
    this._agentStepsActionsService
      .setActionsForAgentSteps([step], [tempView])
      .then(() => {
        this.menuActions$.next((tempView as AiAgentStepActivityTableRowViewModel).menuActions || []);
      })
      .catch((error: unknown) => {
        this._httpErrorHandlerService.handle(error as HttpErrorResponse);
      });
  }

  private _isRsaMockStep(): boolean {
    return this.agentStep()?.startsWith('rsa-');
  }

  private _isIamDemoEnabled(): boolean {
    return this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.SpmIamDemoAmex);
  }

  private static readonly _IAM_DEMO_STEP_IDS = new Set([
    'step1-user-message-morning-briefing',
    'step2-tool-request-read-emails',
    'step3-tool-response-read-emails',
    'step4-tool-request-send-email-exfil',
    'step5-tool-response-send-email-exfil',
    'step6-ai-message-cover-response',
    'spaiware-step-user-upload',
    'spaiware-step-memory-update',
    'spaiware-step-benign-output',
    'spaiware-step-benign-message',
    'spaiware-step-http-exfil',
    'cursor-mcp-step1-session-start',
    'cursor-mcp-step2-query-prod-db',
    'cursor-mcp-step3-read-pii',
    'cursor-mcp-step4-connect-filesystem',
    'cursor-mcp-step5-exfil-write',
  ]);

  private _isIamDemoMockStep(): boolean {
    return this._isIamDemoEnabled() && AgentStepComponent._IAM_DEMO_STEP_IDS.has(this.agentStep());
  }

  private _isAnyDemoMockStep(): boolean {
    return this._isRsaMockStep() || this._isIamDemoMockStep();
  }

  private _loadMockStepById(stepId: string): Observable<AgentStepModel> {
    const rsaMock$ = this._httpClient
      .get<AgentStepModel[]>('assets/mockData/rsaActivityMockData.json')
      .pipe(catchError(() => of([] as AgentStepModel[])));

    const iamMock$ = this._isIamDemoEnabled()
      ? this._httpClient
          .get<AgentStepModel[]>('assets/mockData/iamDemoActivityMockData.json')
          .pipe(catchError(() => of([] as AgentStepModel[])))
      : of([] as AgentStepModel[]);

    return combineLatest([rsaMock$, iamMock$]).pipe(
      map(([rsaSteps, iamSteps]) => {
        const allSteps = [...iamSteps, ...rsaSteps];
        const found = allSteps.find((s) => s.id === stepId);
        if (!found) {
          throw new Error(`Mock step ${stepId} not found`);
        }
        return found;
      })
    );
  }

  private _fetchRsaMockSensitiveData(stepId: string): Observable<ActionGetAgentStepSensitiveDataResponseModel> {
    const notFound = {
      resultCode: 'error',
      title: 'Not Found',
      transcript: '',
    };
    type ContentMap = Record<string, ActionGetAgentStepSensitiveDataResponseModel>;

    const rsa$ = this._httpClient
      .get<ContentMap>('assets/mockData/rsaFetchContentMockData.json')
      .pipe(catchError(() => of({} as ContentMap)));

    const iam$ = this._isIamDemoEnabled()
      ? this._httpClient
          .get<ContentMap>('assets/mockData/iamDemoFetchContentMockData.json')
          .pipe(catchError(() => of({} as ContentMap)))
      : of({} as ContentMap);

    return combineLatest([rsa$, iam$]).pipe(
      map(([rsaData, iamData]) => {
        return iamData[stepId] ?? rsaData[stepId] ?? notFound;
      })
    );
  }

  private _getSensitiveDataMethod(
    action: SensitiveDataRequestActions,
    sourceId?: string
  ): () => Observable<ActionGetAgentStepSensitiveDataResponseModel> {
    if (this._isAnyDemoMockStep()) {
      return () => this._fetchRsaMockSensitiveData(this.agentStep());
    }
    return () =>
      this._aiAgentStepsService.getAgentStepSensitiveData(this.integration(), this.agentStep(), action, sourceId);
  }

  private _fetchMockFindingsByThreadId(threadId: string): void {
    type FindingsMap = Record<string, FindingModel>;

    const rsa$ = this._httpClient
      .get<FindingsMap>('assets/mockData/rsaFindingsMockData.json')
      .pipe(catchError(() => of({} as FindingsMap)));

    const iam$ = this._isIamDemoEnabled()
      ? this._httpClient
          .get<FindingsMap>('assets/mockData/iamDemoFindingsMockData.json')
          .pipe(catchError(() => of({} as FindingsMap)))
      : of({} as FindingsMap);

    combineLatest([rsa$, iam$]).subscribe(([rsaData, iamData]) => {
      const all = [...Object.values(iamData), ...Object.values(rsaData)];
      const matched = all.filter((f) => f.thread?.id === threadId);
      const response = {
        _embedded: { items: matched },
        _links: { self: { href: '' } },
        page: {
          number: 1,
          size: matched.length,
          totalItems: matched.length,
          totalPages: 1,
        },
      } as JsonHalCollection<FindingModel>;
      this._handleThreadIdsResponse(response);
      this.isThreadIdsLoading$.next(false);
    });
  }

  private _fetchFindingsByThreadId(integrationId: string, threadId: string) {
    const isInline = this.isInlineView();
    const sourcePipeline = isInline ? 'inline' : undefined;
    const findings$ = isInline
      ? this._searchActivityHttpService.getInLineFindingsByThreadId(threadId)
      : this._aiFindingsService.getFindingsByThreadId(threadId, sourcePipeline);

    findings$.subscribe(
      (findings: JsonHalCollection<FindingModel>) => {
        this._handleThreadIdsResponse(findings);
        this.isThreadIdsLoading$.next(false);
      },
      (error: HttpErrorResponse) => {
        if (error.status !== 404) this._httpErrorHandlerService.handle(error);
        this.isThreadIdsLoading$.next(false);
      }
    );
  }
}
